import { TRACKERS } from "@/constants/Trackers";
import type {
  AiFeedItem,
  SnomedCodeSelection,
} from "@/features/AiFeed/network/types";
import { isCreateConditionItem } from "@/features/AiFeed/network/types";
import { trackEvent } from "@/lib/tracking";
import { useState } from "react";
import { useAcceptAiFeedItem } from "../../network/useAcceptAiFeedItem";
import { useRejectAiFeedItem } from "../../network/useRejectAiFeedItem";

export interface UseCreateConditionActionsResult {
  suggestions: SnomedCodeSelection[];
  selectedCode: SnomedCodeSelection | null;
  onSelect: (code: SnomedCodeSelection) => void;
  handleAccept: () => void;
  handleReject: () => void;
  isAccepting: boolean;
  isRejecting: boolean;
  isMutating: boolean;
  canAccept: boolean;
}

export function useCreateConditionActions(
  item: AiFeedItem,
  encounterId: string,
): UseCreateConditionActionsResult {
  const isCreate = isCreateConditionItem(item);
  const suggestions = isCreate ? item.intent.suggestions : [];

  const [selectedCode, setSelectedCode] = useState<SnomedCodeSelection | null>(
    () => (suggestions.length > 0 ? suggestions[0] : null),
  );

  const { mutate: accept, isPending: isAccepting } = useAcceptAiFeedItem(
    item.patientId,
    encounterId,
    item.aiFeedItemId,
  );
  const { mutate: reject, isPending: isRejecting } = useRejectAiFeedItem(
    item.patientId,
    encounterId,
    item.aiFeedItemId,
  );

  const isMutating = isAccepting || isRejecting;
  const canAccept = selectedCode !== null && !isMutating;

  const onSelect = (code: SnomedCodeSelection) => {
    setSelectedCode(code);
  };

  const handleAccept = () => {
    if (!selectedCode) return;
    trackEvent(TRACKERS.ACCEPT_AI_FEED_ITEM);
    // No optimistic accept; wait for success before showing resolved state
    accept({ itemId: item.aiFeedItemId, selectedCode });
  };

  const handleReject = () => {
    trackEvent(TRACKERS.REJECT_AI_FEED_ITEM);
    reject({ itemId: item.aiFeedItemId });
  };

  return {
    suggestions,
    selectedCode,
    onSelect,
    handleAccept,
    handleReject,
    isAccepting,
    isRejecting,
    isMutating,
    canAccept,
  };
}
